import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextInput } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { ImageField } from 'src/components/Storefront/ImageField';
import { SortableList, SortableRow, moveItem } from 'src/components/Storefront/SortableList';
import { StorefrontTestimonialDto } from 'src/services/apis/types';

/** The API's caps (spec §3): 12 testimonials, 280 characters of quote. The author and the role
 * are capped at 120 by the DTO's own `@MaxLength`, so the inputs carry it too. */
export const TESTIMONIAL_MAX = 12;
export const TESTIMONIAL_QUOTE_MAX = 280;
export const TESTIMONIAL_AUTHOR_MAX = 120;
export const TESTIMONIAL_ROLE_MAX = 120;

/** `role` and `avatarFileId` are optional on the wire, so the editor holds them as a string and a
 * nullable id, and maps a blank one back to an omitted key in the payload. */
export type TestimonialRow = { quote: string; author: string; role: string; avatarFileId: string | null };

/**
 * Coerces whatever the jsonb column holds into rows the editor can render. The generated entity
 * types `testimonials` loosely (Swagger cannot describe a jsonb array's items), so this is the one
 * place that decides what a stored item looks like — and it never throws on a hand-edited row.
 */
export function toTestimonialRows(input: unknown): TestimonialRow[] {
  const stored = Array.isArray(input) ? input : [];
  return stored
    .filter(
      (raw): raw is { quote?: unknown; author?: unknown; role?: unknown; avatarFileId?: unknown } =>
        typeof raw === 'object' && raw !== null
    )
    .map((raw) => ({
      quote: typeof raw.quote === 'string' ? raw.quote : '',
      author: typeof raw.author === 'string' ? raw.author : '',
      role: typeof raw.role === 'string' ? raw.role : '',
      avatarFileId: typeof raw.avatarFileId === 'string' && raw.avatarFileId !== '' ? raw.avatarFileId : null,
    }));
}

/**
 * A testimonial needs a quote and an author — the API's two required strings — so a half-filled
 * row is dropped rather than sent, and the list is capped so a stored row cannot cause a 422.
 */
export function toTestimonialPayload(rows: TestimonialRow[]): StorefrontTestimonialDto[] {
  return rows
    .filter((row) => row.quote.trim() !== '' && row.author.trim() !== '')
    .slice(0, TESTIMONIAL_MAX)
    .map((row) => {
      const quote = row.quote.trim();
      const author = row.author.trim();
      const role = row.role.trim();
      // A blank role and a missing avatar omit their keys instead of sending `null`: Swagger
      // expresses no nullability, so the generated DTO is `role?: string` / `avatarFileId?: string`,
      // and the server treats missing and null identically.
      return {
        quote,
        author,
        ...(role === '' ? {} : { role }),
        ...(row.avatarFileId === null ? {} : { avatarFileId: row.avatarFileId }),
      };
    });
}

interface TestimonialsEditorProps {
  items: TestimonialRow[];
  onChange: (next: TestimonialRow[]) => void;
}

const TestimonialsEditor = React.memo(({ items, onChange }: TestimonialsEditorProps) => {
  const { t } = useTranslation();

  const update = useCallback(
    (index: number, patch: Partial<TestimonialRow>) => {
      onChange(items.map((row, position) => (position === index ? { ...row, ...patch } : row)));
    },
    [items, onChange]
  );

  const remove = useCallback(
    (index: number) => {
      onChange(items.filter((_row, position) => position !== index));
    },
    [items, onChange]
  );

  const add = useCallback(() => {
    onChange([...items, { quote: '', author: '', role: '', avatarFileId: null }]);
  }, [items, onChange]);

  const reorder = useCallback(
    (from: number, to: number) => {
      onChange(moveItem(items, from, to));
    },
    [items, onChange]
  );

  const atCap = items.length >= TESTIMONIAL_MAX;

  return (
    <fieldset className={'storefront-extras__group'}>
      <legend className={'cds--visually-hidden'}>{t('storefront.extras.testimonials')}</legend>
      <p className={'storefront-extras__hint'}>{t('storefront.extras.testimonialsHint')}</p>

      {items.length === 0 && <p className={'storefront-extras__empty'}>{t('storefront.extras.testimonialsEmpty')}</p>}

      {items.length > 0 && (
        <SortableList ids={items.map((_row, index) => String(index))} onReorder={reorder}>
          {items.map((row, index) => (
            <SortableRow
              key={index}
              id={String(index)}
              removeLabel={t('storefront.extras.removeTestimonial')}
              onRemove={() => remove(index)}
            >
              <TextInput
                id={`testimonial-quote-${index}`}
                labelText={t('storefront.extras.quote')}
                maxLength={TESTIMONIAL_QUOTE_MAX}
                value={row.quote}
                onChange={(event) => update(index, { quote: event.target.value })}
              />
              <TextInput
                id={`testimonial-author-${index}`}
                labelText={t('storefront.extras.author')}
                maxLength={TESTIMONIAL_AUTHOR_MAX}
                value={row.author}
                onChange={(event) => update(index, { author: event.target.value })}
              />
              <TextInput
                id={`testimonial-role-${index}`}
                labelText={t('storefront.extras.role')}
                helperText={t('storefront.extras.roleHint')}
                maxLength={TESTIMONIAL_ROLE_MAX}
                value={row.role}
                onChange={(event) => update(index, { role: event.target.value })}
              />
              <ImageField
                title={t('storefront.extras.avatar')}
                description={t('storefront.extras.avatarHint')}
                aspectRatio={'1x1'}
                cropperAspectRatio={1}
                value={row.avatarFileId}
                onChange={(fileId) => update(index, { avatarFileId: fileId })}
              />
            </SortableRow>
          ))}
        </SortableList>
      )}

      <div className={'storefront-extras__add'}>
        <Button renderIcon={Add} kind={'tertiary'} size={'sm'} disabled={atCap} onClick={add}>
          {t('storefront.extras.addTestimonial')}
        </Button>
        {atCap && <p className={'storefront-extras__empty'}>{t('storefront.extras.testimonialsFull')}</p>}
      </div>
    </fieldset>
  );
});

TestimonialsEditor.displayName = 'TestimonialsEditor';

export { TestimonialsEditor };
export default TestimonialsEditor;
